/**
 * Session Event System
 * Handles real-time session change events and cross-tab synchronization
 */

export type SessionEventType =
  | "session-changed"
  | "session-invalidated"
  | "auth-state-changed"
  | "cache-invalidated"
  | "session-refreshed";

export interface SessionEvent {
  type: SessionEventType;
  timestamp: number;
  data?: any;
  source: "local" | "storage" | "network";
  tabId: string;
}

export interface SessionEventListener {
  (event: SessionEvent): void;
}

export interface SessionEventSystem {
  emit(
    type: SessionEventType,
    data?: any,
    source?: "local" | "storage" | "network",
  ): void;
  on(type: SessionEventType, listener: SessionEventListener): () => void;
  off(type: SessionEventType, listener: SessionEventListener): void;
  destroy(): void;
}

class SessionEventSystemImpl implements SessionEventSystem {
  private listeners = new Map<SessionEventType, Set<SessionEventListener>>();
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private readonly tabId: string;
  private readonly storageKey = "session-events";

  constructor() {
    this.tabId = this.generateTabId();
    this.setupStorageListener();
  }

  emit(
    type: SessionEventType,
    data?: any,
    source: "local" | "storage" | "network" = "local",
  ): void {
    const event: SessionEvent = {
      type,
      timestamp: Date.now(),
      data,
      source,
      tabId: this.tabId,
    };

    // Emit to local listeners
    this.emitToLocalListeners(event);

    // Broadcast to other tabs via localStorage (only for local events)
    if (source === "local" && typeof window !== "undefined") {
      this.broadcastToOtherTabs(event);
    }
  }

  on(type: SessionEventType, listener: SessionEventListener): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type)!.add(listener);

    // Return unsubscribe function
    return () => {
      this.off(type, listener);
    };
  }

  off(type: SessionEventType, listener: SessionEventListener): void {
    const typeListeners = this.listeners.get(type);
    if (typeListeners) {
      typeListeners.delete(listener);
      if (typeListeners.size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  destroy(): void {
    this.listeners.clear();

    if (this.storageListener && typeof window !== "undefined") {
      window.removeEventListener("storage", this.storageListener);
      this.storageListener = null;
    }
  }

  private emitToLocalListeners(event: SessionEvent): void {
    const typeListeners = this.listeners.get(event.type);
    if (typeListeners) {
      typeListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          console.error(
            `Error in session event listener for ${event.type}:`,
            error,
          );
        }
      });
    }
  }

  private broadcastToOtherTabs(event: SessionEvent): void {
    try {
      const eventData = JSON.stringify(event);
      localStorage.setItem(this.storageKey, eventData);

      // Clear the storage item immediately to allow repeated events
      setTimeout(() => {
        try {
          localStorage.removeItem(this.storageKey);
        } catch (_error) {
          // Ignore cleanup errors
        }
      }, 100);
    } catch (error) {
      console.error("Failed to broadcast session event to other tabs:", error);
    }
  }

  private setupStorageListener(): void {
    if (typeof window === "undefined") return;

    this.storageListener = (storageEvent: StorageEvent) => {
      if (storageEvent.key === this.storageKey && storageEvent.newValue) {
        try {
          const event: SessionEvent = JSON.parse(storageEvent.newValue);

          // Don't process events from the same tab
          if (event.tabId === this.tabId) return;

          // Mark as storage event and emit to local listeners
          event.source = "storage";
          this.emitToLocalListeners(event);
        } catch (error) {
          console.error("Failed to parse session event from storage:", error);
        }
      }
    };

    window.addEventListener("storage", this.storageListener);
  }

  private generateTabId(): string {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton instance
export const sessionEventSystem = new SessionEventSystemImpl();

// Cleanup on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    sessionEventSystem.destroy();
  });
}
